import {showMessage} from 'exsportia-components'
import {Formik, FormikConfig, FormikProps} from 'formik'
import {FC, useCallback, useMemo, useRef, useState} from 'react'
import {Outlet, useNavigate, useParams} from 'react-router-dom'
import {number, object, string} from 'yup'

import {BookingFormData} from '../../../api/forms/types'
import {PromoError} from '../../../api/promo-codes/types'
import {isCachedOpnChargeDegraded} from '../../../api/provider-status/get-provider-status.query'
import {
  useBuySubscriptionByOneTimePayment,
  useBuySubscriptionByToken,
  useSubscription,
} from '../../../api/subscriptions/hooks'
import {PriceOption, SubscriptionData, SubscriptionPriceTypesEnum} from '../../../api/subscriptions/types'
import {User} from '../../../api/user/types'
import {useVenue} from '../../../api/venue/hooks'
import {Venue} from '../../../api/venue/types'
import {handlePromoPayError} from '../../../components/promo-code-field/handle-promo-pay-error'
import {isContactBlockedError, showBlockedMessage} from '../../../helpers/blocked-member'
import {collisionSafeUidMaker} from '../../../helpers/logic'
import {CycleOption, getAdditionalInstallmentsData} from '../../../pages/subscription-profile/helpers'
import {l} from '../../../utils/i18n'
import {useBookingLoader} from '../../booking/hooks'
import {withChargeRetryHint} from '../../payments/helpers/checkout-gate'
import {useHandleDraftEntityStatusChange} from '../../payments/hooks/handle-draft-entity-status-change.hook'
import {usePaymentProvider} from '../../payments/hooks/payment-provider-form.provider'
import {PaymentTypes} from '../../payments/hooks/use-payment-options'

export type Params = {
  subscriptionId?: string
}

export type SubscriptionFormType = {
  subscriptionId: SubscriptionData['uid'] | null
  subscription: SubscriptionData | null
  plan: SubscriptionData['prices'][number] | null
  paymentType: PaymentTypes | null
  paymentMethodToken: string | null
  totalPrice: number | null
  promoCode?: string | null
  promoRejection?: PromoError | null
  // List amount of the first cycle, sent as ?firstCyclePrice= so the server prices it exactly.
  promoFirstCyclePrice?: number | null
  subscriptionBookingId: string
  selectedStartDate?: number | null
  installmentOption: CycleOption['installmentsOptions'][number] | null
  formData?: BookingFormData
  subscriptionDraft: {
    endDate: number
    currency: Venue['currency']
    startDate: number
    checkActiveStatus: boolean
    plan: PriceOption
    cycles: {price: number; toPay: boolean; startDate: number; endDate: number}[]
    userId: string
    defaultCyclePrice: number
    systemType: string,
    venueId: string
    membershipId: string
    priceTime: SubscriptionPriceTypesEnum
    userName: User['name']
    promoCode?: string | null
    isPaidAdditionalFee?: boolean
    additionalFee?: PriceOption['additionalFee']
    installmentAmount?: number
    form?: BookingFormData
  } | null
}

export const PurchaseSubscriptionForm: FC = ({}) => {
  const {subscriptionId: subId} = useParams<Params>()
  const navigate = useNavigate()
  const {openPaymentForm} = usePaymentProvider()
  const {setIsLoading: setIsBookingLoading} = useBookingLoader()
  const {data: subscription} = useSubscription(subId)
  const {data: venue} = useVenue()
  const formikRef = useRef<FormikProps<SubscriptionFormType>>(null)
  const setFormFieldValue = useCallback((field: string, value: unknown) => {
    void formikRef.current?.setFieldValue(field, value)
  }, [])

  const {mutate: payByToken} = useBuySubscriptionByToken({
    onMutate: () => {
      formikRef.current?.setSubmitting(true)
    },
    onSuccess: (_, options) => {
      if (venue) {
        navigate(`/${venue.uid}/subscription-bookings/${options.entityId}/profile`, {replace: true})
      }
      showMessage(l('success'), 'success')
    },
    onError: (e) => {
      if (isContactBlockedError(e)) {
        return showBlockedMessage()
      }
      if (handlePromoPayError(e, setFormFieldValue, 'getSubscriptionQuery')) {
        return
      }
      // Opn charge DEGRADED: never echo the provider's text; say what to do instead.
      if (isCachedOpnChargeDegraded(venue?.uid)) {
        showMessage(withChargeRetryHint(l('somethingWentWrong'), l('checkout-charge-retry-hint'), true), 'error')
        return
      }
      const errorData = e.response?.data as {message?: string} | undefined
      const errorMessage = errorData?.message || l('somethingWentWrong')
      showMessage(errorMessage, 'error')
      console.log(String(e))
    },
    onSettled: () => {
      formikRef.current?.setSubmitting(false)
    },
  })

  const {mutate: payByOneTimePayment} = useBuySubscriptionByOneTimePayment({
    onMutate: () => {
      formikRef.current?.setSubmitting(true)
    },
    onError: (e) => {
      handlePromoPayError(e, setFormFieldValue, 'getSubscriptionQuery')
      console.log(String(e))
    },
    onSettled: () => {
      formikRef.current?.setSubmitting(false)
    },
  })

  const [isLoading, setIsLoading] = useState(false)
  const [finalEntityId, setFinalEntityId] = useState<string | null>(null)

  const initialValues: SubscriptionFormType = useMemo(
    () => ({
      subscriptionId: subId || null,
      subscription: null,
      totalPrice: null,
      promoCode: null,
      promoRejection: null,
      promoFirstCyclePrice: null,
      plan: null,
      paymentType: null,
      installmentOption: null,
      paymentMethodToken: null,
      subscriptionDraft: null,
      selectedStartDate: null,
      subscriptionBookingId: collisionSafeUidMaker(),
    }),
    [subId, subscription],
  )

  const updateLoadingState = useCallback((val: boolean) => {
    if (!val) {
      setFinalEntityId(null)
    }
    setIsLoading(val)
  }, [])

  const {setDraftPendingStatus} = useHandleDraftEntityStatusChange({
    entity: 'subscription',
    entityId: finalEntityId,
    isLoading,
    setIsLoading: updateLoadingState,
  })
  const onSubmit = useCallback<FormikConfig<SubscriptionFormType>['onSubmit']>(
    (values, {setSubmitting}) => {
      let additionalInstallmentsData = null

      // Check for installment option and set additional installment data
      if (values.installmentOption && values.plan?.type === SubscriptionPriceTypesEnum.ONE_TIME) {
        additionalInstallmentsData = getAdditionalInstallmentsData(values.installmentOption)
      }
      const IsPayNowOrPriceZero =
        (values.paymentType === PaymentTypes.PAY_NOW && values.paymentMethodToken) || values.totalPrice === 0
      // Handle PAY_NOW payment type
      if (values.subscriptionDraft && venue && values.subscriptionId && IsPayNowOrPriceZero) {
        payByToken({
          sum: Number(values.totalPrice),
          entityId: values.subscriptionBookingId,
          venueId: venue.uid,
          currency: venue.currency,
          paymentMethodId: values.paymentMethodToken,
          data: {
            ...values.subscriptionDraft,
            startDate: values.subscriptionDraft.startDate,
            installmentAmount: additionalInstallmentsData?.installmentAmount,
            form: values.formData,
          },
          ...additionalInstallmentsData,
        })
        return
      }

      // Handle ONE_TIME payment type
      if (values.subscriptionDraft && venue && values.paymentType === PaymentTypes.ONE_TIME) {
        payByOneTimePayment(
          {
            draft: {
              ...values.subscriptionDraft,
              startDate: Number(values.subscriptionDraft.startDate),
              systemType: 'BOOKING_WEB',
              form: values.formData,
            },
            entityId: values.subscriptionBookingId,
            totalPrice: values.totalPrice,
          },
          {
            onSuccess: (_, options) => {
              if (!options.totalPrice) {
                return
              }
              const resolvedEntityId = options.entityId || values.subscriptionBookingId
              setFinalEntityId(resolvedEntityId)
              setDraftPendingStatus(resolvedEntityId, () => {
                updateLoadingState(true)
                openPaymentForm?.({
                  entity: 'subscription',
                  bookingId: resolvedEntityId,
                  totalPrice: options.totalPrice ?? undefined,
                  startProcessCallback: () => setIsBookingLoading(true),
                })
              })
            },
          },
        )
        setSubmitting(false)
        return
      }

      // Default case to stop submission
      setSubmitting(false)
    },
    [
      venue,
      finalEntityId,
      payByToken,
      payByOneTimePayment,
      setFinalEntityId,
      setDraftPendingStatus,
      updateLoadingState,
      openPaymentForm,
      setIsBookingLoading,
    ],
  )

  const validationSchema = useMemo(
    () =>
      object().shape({
        subscriptionId: string().required(l('required')),
        totalPrice: number().required(l('required')),
        subscription: object().required(l('required')),
        plan: object().required(l('required')),
        subscriptionDraft: object().required(l('required')),
      }),
    [],
  )

  return (
    <>
      <Formik<SubscriptionFormType>
        innerRef={formikRef}
        onSubmit={onSubmit}
        initialValues={initialValues}
        validationSchema={validationSchema}
      >
        <Outlet />
      </Formik>
    </>
  )
}
